/* ==========================================================================
   whero-status-pill.js — Wynston's closest-shop status pill
   --------------------------------------------------------------------------
   Reads shop data from <script id="whero-shops-data"> (emitted by
   snippets/whero-status-pill.liquid), computes each shop's current state in
   America/Los_Angeles, resolves the user's position via a cache → IP geo →
   GPS-on-click chain, sorts shops by Haversine distance, and updates every
   .whero-status-pill in the DOM.

   Dispatches `whero:status-updated` on document once a closest shop is
   known:
     detail = { closestShop, state, distanceMi, allShops }

   Exposes window.WheroStatusPill = { refresh(), getState() } for debugging.

   Accessibility:
     - Ensures role="button" + tabindex="0" on every pill.
     - Enter/Space invokes the same handler as click.
     - aria-live="polite" on each pill's state-label so updates are
       announced non-intrusively.
   ========================================================================== */
(function () {
  'use strict';

  var SHOPS_DATA_ID = 'whero-shops-data';
  var PILL_SELECTOR = '.whero-status-pill';
  var LA_TZ = 'America/Los_Angeles';
  var POS_CACHE_KEY = 'whero_user_pos';
  var POS_TTL_MS = 6 * 60 * 60 * 1000; // 6h
  var IP_GEO_URL = 'https://ipapi.co/json/';
  var IP_GEO_TIMEOUT_MS = 4000;
  var GPS_TIMEOUT_MS = 5000;
  var DEFAULT_SHOP_TOKEN = 'san marcos'; // matched loosely against name/nickname/city
  var EARTH_RADIUS_MI = 3958.8;
  // When the user's resolved position is more than this many miles from
  // the closest shop, hide the pill entirely. These visitors are here
  // for overnight shipping, not a drive — the open/close/closed chrome
  // is noise for them. Threshold is generous enough to stay on for the
  // whole San Diego / North County / LA-ish commute belt.
  var NEAR_THRESHOLD_MI = 60;

  /* ---- data loading ------------------------------------------------------ */

  function readShops() {
    var el = document.getElementById(SHOPS_DATA_ID);
    if (!el) return null;
    try {
      var parsed = JSON.parse(el.textContent || 'null');
      if (!Array.isArray(parsed) || parsed.length === 0) return null;
      // Shopify's `| json` filter serializes metaobject decimal fields as
      // strings, not numbers. Coerce here so rankShops' numeric checks
      // pass and the >60mi shipping-mode swap can fire.
      for (var i = 0; i < parsed.length; i++) {
        var lat = parseFloat(parsed[i].lat);
        var lng = parseFloat(parsed[i].lng);
        parsed[i].lat = isNaN(lat) ? null : lat;
        parsed[i].lng = isNaN(lng) ? null : lng;
      }
      return parsed;
    } catch (e) {
      return null;
    }
  }

  /* ---- LA-local time helpers --------------------------------------------- */

  function nowInLA() {
    // Intl.DateTimeFormat respects the tz arg on every modern browser.
    var fmt = new Intl.DateTimeFormat('en-US', {
      timeZone: LA_TZ,
      weekday: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
    var parts = fmt.formatToParts(new Date());
    var bag = {};
    for (var i = 0; i < parts.length; i++) bag[parts[i].type] = parts[i].value;
    var hour = parseInt(bag.hour, 10) % 24; // some engines emit '24' at midnight
    var minute = parseInt(bag.minute, 10);
    var day = (bag.weekday || '').toLowerCase().slice(0, 3); // 'mon'…'sun'
    return { day: day, mins: hour * 60 + minute };
  }

  function hhmmToMins(hhmm) {
    if (typeof hhmm !== 'string') return NaN;
    var parts = hhmm.split(':');
    if (parts.length !== 2) return NaN;
    var h = parseInt(parts[0], 10);
    var m = parseInt(parts[1], 10);
    if (isNaN(h) || isNaN(m)) return NaN;
    return h * 60 + m;
  }

  function formatClockTime(hhmm) {
    var parts = (hhmm || '').split(':');
    if (parts.length !== 2) return '';
    var h = parseInt(parts[0], 10);
    var m = parts[1];
    if (isNaN(h)) return '';
    var displayH, ampm;
    if (h === 0)        { displayH = 12; ampm = 'am'; }
    else if (h < 12)    { displayH = h;  ampm = 'am'; }
    else if (h === 12)  { displayH = 12; ampm = 'pm'; }
    else                { displayH = h - 12; ampm = 'pm'; }
    return m === '00' ? (displayH + ampm) : (displayH + ':' + m + ampm);
  }

  /* ---- per-shop state ---------------------------------------------------- */

  function shopState(shop, now) {
    var hours = shop && shop.hours;
    if (!hours || typeof hours !== 'object') return 'is-closed';
    var today = hours[now.day];
    if (!today || !today.open || !today.close) return 'is-closed';

    var openMins = hhmmToMins(today.open);
    var closeMins = hhmmToMins(today.close);
    if (isNaN(openMins) || isNaN(closeMins) || closeMins <= openMins) return 'is-closed';

    if (now.mins < openMins || now.mins >= closeMins) return 'is-closed';
    if (closeMins - now.mins <= 30) return 'is-closing';
    return 'is-open';
  }

  function stateLabel(state) {
    if (state === 'is-open') return 'Open';
    if (state === 'is-closing') return 'Closing soon';
    return 'Closed';
  }

  /* ---- position resolution ---------------------------------------------- */

  function readCachedPosition() {
    try {
      var raw = localStorage.getItem(POS_CACHE_KEY);
      if (!raw) return null;
      var c = JSON.parse(raw);
      if (typeof c.lat !== 'number' || typeof c.lng !== 'number') return null;
      if (!c.ts || (Date.now() - c.ts) > POS_TTL_MS) return null;
      return { lat: c.lat, lng: c.lng, source: c.source || 'cache' };
    } catch (_) {
      return null;
    }
  }

  function cachePosition(pos) {
    try {
      localStorage.setItem(POS_CACHE_KEY, JSON.stringify({
        lat: pos.lat, lng: pos.lng, source: pos.source, ts: Date.now()
      }));
    } catch (_) { /* storage disabled — non-fatal */ }
  }

  function fetchIpGeo() {
    if (typeof fetch !== 'function') return Promise.resolve(null);
    var ctl;
    try { ctl = new AbortController(); } catch (_) { ctl = null; }
    var opts = ctl ? { signal: ctl.signal } : {};
    var timer = ctl ? setTimeout(function () { ctl.abort(); }, IP_GEO_TIMEOUT_MS) : null;
    return fetch(IP_GEO_URL, opts).then(function (r) {
      if (timer) clearTimeout(timer);
      if (!r.ok) return null;
      return r.json();
    }).then(function (j) {
      if (!j) return null;
      var lat = parseFloat(j.latitude);
      var lng = parseFloat(j.longitude);
      if (isNaN(lat) || isNaN(lng)) return null;
      return { lat: lat, lng: lng, source: 'ip' };
    }).catch(function () {
      if (timer) clearTimeout(timer);
      return null;
    });
  }

  function requestGeolocation() {
    return new Promise(function (resolve, reject) {
      if (!navigator.geolocation) return reject(new Error('geolocation unavailable'));
      navigator.geolocation.getCurrentPosition(
        function (p) {
          resolve({ lat: p.coords.latitude, lng: p.coords.longitude, source: 'gps' });
        },
        function (err) { reject(err); },
        { timeout: GPS_TIMEOUT_MS, maximumAge: 10 * 60 * 1000 }
      );
    });
  }

  /* ---- distance ---------------------------------------------------------- */

  function haversineMi(lat1, lng1, lat2, lng2) {
    var toRad = function (d) { return d * Math.PI / 180; };
    var dLat = toRad(lat2 - lat1);
    var dLng = toRad(lng2 - lng1);
    var a = Math.sin(dLat / 2) * Math.sin(dLat / 2)
          + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2))
          * Math.sin(dLng / 2) * Math.sin(dLng / 2);
    return 2 * EARTH_RADIUS_MI * Math.asin(Math.sqrt(a));
  }

  function formatDistance(mi) {
    if (typeof mi !== 'number' || isNaN(mi)) return '';
    if (mi < 10) return mi.toFixed(1) + ' mi';
    return Math.round(mi) + ' mi';
  }

  /* ---- shipping day calculator ------------------------------------------- */
  // Delegates to the shared module at assets/whero-shipping-schedule.js so
  // schedule + blackout rules live in one place. Returns the delivery info
  // object (see WheroShipping.computeDelivery) or null if the deferred
  // module hasn't loaded yet — applyShippingMode then leaves the label
  // alone and the next refresh tick will fill it.

  function getShippingInfo() {
    if (window.WheroShipping && typeof window.WheroShipping.computeDelivery === 'function') {
      try { return window.WheroShipping.computeDelivery(); } catch (_) { return null; }
    }
    return null;
  }

  function applyShippingMode(pillEl, shipping) {
    pillEl.classList.add('whero-status-pill--shipping');
    var shippingView = pillEl.querySelector('[data-role="shipping-view"]');
    var shippingLabel = pillEl.querySelector('[data-role="shipping-label"]');
    if (shippingView) shippingView.removeAttribute('hidden');
    if (shippingLabel && shipping && shipping.dateLabel) {
      shippingLabel.textContent = 'Order now \u2014 get ice cream on ' + shipping.dateLabel;
    }
    var shippingUrl = pillEl.getAttribute('data-shipping-url') || '/collections/shipping';
    pillEl.setAttribute('href', shippingUrl);
    pillEl.setAttribute('title', 'Shop shippable pints');
    // Hide the pickup row below the pill.
    var pickupRow = pillEl.nextElementSibling;
    if (pickupRow && pickupRow.getAttribute('data-role') === 'order-cta') {
      pickupRow.setAttribute('hidden', '');
    }
    // Hide the desktop companion pill too — shipping visitors don't pickup.
    var companionPill = pillEl.parentElement
      ? pillEl.parentElement.querySelector('[data-role="order-cta-desktop"]')
      : null;
    if (companionPill) companionPill.setAttribute('hidden', '');
  }

  function removeShippingMode(pillEl) {
    pillEl.classList.remove('whero-status-pill--shipping');
    var shippingView = pillEl.querySelector('[data-role="shipping-view"]');
    if (shippingView) shippingView.setAttribute('hidden', '');
  }

  /* ---- DOM update -------------------------------------------------------- */

  function setHidden(el, hidden) {
    if (!el) return;
    if (hidden) el.setAttribute('hidden', '');
    else el.removeAttribute('hidden');
  }

  function applyToPill(pillEl, viewModel) {
    pillEl.classList.remove('is-open', 'is-closing', 'is-closed');
    pillEl.classList.add(viewModel.state);

    var labelEl = pillEl.querySelector('[data-role="state-label"]');
    if (labelEl) {
      var strong = labelEl.querySelector('strong');
      var text = stateLabel(viewModel.state);
      if (strong) strong.textContent = text;
      else labelEl.textContent = text;
    }

    var isCompact = pillEl.classList.contains('whero-status-pill--compact');
    var closeEl = pillEl.querySelector('[data-role="close-time"]');
    var closeSep = pillEl.querySelector('[data-role="close-sep"]');
    if (closeEl) {
      if (!isCompact && viewModel.closeClock && viewModel.state !== 'is-closed') {
        closeEl.textContent = 'Closes ' + viewModel.closeClock;
        setHidden(closeEl, false);
        setHidden(closeSep, false);
      } else {
        setHidden(closeEl, true);
        setHidden(closeSep, true);
      }
    }

    var storeEl = pillEl.querySelector('[data-role="store"]');
    if (storeEl) {
      storeEl.textContent = viewModel.shop.nickname || viewModel.shop.name || '';
    }

    var distEl = pillEl.querySelector('[data-role="distance"]');
    var distSep = pillEl.querySelector('[data-role="distance-sep"]');
    if (distEl) {
      if (typeof viewModel.distanceMi === 'number' && !isNaN(viewModel.distanceMi)) {
        distEl.textContent = formatDistance(viewModel.distanceMi);
        setHidden(distEl, false);
        setHidden(distSep, false);
      } else {
        setHidden(distEl, true);
        setHidden(distSep, true);
      }
    }

    // Special hours indicator — amber dot override via CSS attribute selector.
    pillEl.setAttribute('data-special-hours', viewModel.hasSpecialHours ? 'true' : 'false');

    // Special hours label text (e.g. "Early Close 8pm", "Closed for Private
    // Event"). Sourced from the shop's metaobject data already in the
    // payload — no extra fetch.
    var specialSep = pillEl.querySelector('[data-role="special-sep"]');
    var specialLabel = pillEl.querySelector('[data-role="special-label"]');
    if (specialLabel) {
      var label = (viewModel.shop && viewModel.shop.specialHoursLabel) || '';
      if (viewModel.hasSpecialHours && label) {
        specialLabel.textContent = label;
        setHidden(specialLabel, false);
        setHidden(specialSep, false);
      } else {
        setHidden(specialLabel, true);
        setHidden(specialSep, true);
      }
    }

    // Update href to the closest shop's maps URL (location mode).
    if (viewModel.shop && viewModel.shop.mapsUrl) {
      pillEl.setAttribute('href', viewModel.shop.mapsUrl);
      pillEl.setAttribute('title', 'Get directions to ' + (viewModel.shop.nickname || viewModel.shop.name || ''));
    }

    // Pickup row (sibling <a> below the pill): show when open/closing.
    var pickupRow = pillEl.nextElementSibling;
    if (pickupRow && pickupRow.hasAttribute('data-role') && pickupRow.getAttribute('data-role') === 'order-cta') {
      var showOrder = (viewModel.state === 'is-open' || viewModel.state === 'is-closing');
      var orderUrl = (viewModel.shop && viewModel.shop.toastOrderUrl)
        || pillEl.getAttribute('data-toast-order-url')
        || '';
      if (showOrder && orderUrl) {
        pickupRow.setAttribute('href', orderUrl);
        pickupRow.setAttribute('data-order-url', orderUrl);
        setHidden(pickupRow, false);
      } else {
        setHidden(pickupRow, true);
      }
    }

    // Desktop companion pickup pill — sibling of the status pill in the
    // same parent slot. Show/hide + href update follow the same rules as
    // the mobile pickup row above.
    var companionPill = pillEl.parentElement
      ? pillEl.parentElement.querySelector('[data-role="order-cta-desktop"]')
      : null;
    if (companionPill) {
      var showCompanion = (viewModel.state === 'is-open' || viewModel.state === 'is-closing');
      var companionUrl = (viewModel.shop && viewModel.shop.toastOrderUrl)
        || pillEl.getAttribute('data-toast-order-url')
        || '';
      if (showCompanion && companionUrl) {
        companionPill.setAttribute('href', companionUrl);
        companionPill.setAttribute('data-order-url', companionUrl);
        setHidden(companionPill, false);
      } else {
        setHidden(companionPill, true);
      }
    }
  }

  function wirePill(pillEl) {
    // Pill is an <a> — natively focusable and navigable.
    var labelEl = pillEl.querySelector('[data-role="state-label"]');
    if (labelEl && !labelEl.hasAttribute('aria-live')) {
      labelEl.setAttribute('aria-live', 'polite');
    }

    // GPS-on-click: first tap requests Geolocation, refines the closest
    // shop, updates the pill's href, then navigates. Subsequent taps
    // navigate directly (GPS is cached).
    pillEl.addEventListener('click', function (e) {
      if (state.gpsRequested || !navigator.geolocation) return;
      state.gpsRequested = true;
      e.preventDefault();
      requestGeolocation().then(function (pos) {
        state.position = pos;
        cachePosition(pos);
        refresh();
        window.location.href = pillEl.getAttribute('href');
      }).catch(function () {
        window.location.href = pillEl.getAttribute('href');
      });
    });
  }

  /* ---- orchestration ----------------------------------------------------- */

  function pickDefaultShop(shops) {
    for (var i = 0; i < shops.length; i++) {
      var s = shops[i];
      var hay = ((s.nickname || '') + ' ' + (s.name || '') + ' ' + (s.city || '')).toLowerCase();
      if (hay.indexOf(DEFAULT_SHOP_TOKEN) !== -1) return s;
    }
    return shops[0];
  }

  function rankShops(shops, pos, now) {
    var ranked = [];
    for (var i = 0; i < shops.length; i++) {
      var s = shops[i];
      var d = NaN;
      if (pos && typeof s.lat === 'number' && typeof s.lng === 'number') {
        d = haversineMi(pos.lat, pos.lng, s.lat, s.lng);
      }
      ranked.push({ shop: s, state: shopState(s, now), distanceMi: d });
    }
    if (pos) {
      ranked.sort(function (a, b) {
        var da = isNaN(a.distanceMi) ? Infinity : a.distanceMi;
        var db = isNaN(b.distanceMi) ? Infinity : b.distanceMi;
        return da - db;
      });
    }
    return ranked;
  }

  function renderAll(pills, shops, pos) {
    var now = nowInLA();
    var ranked = rankShops(shops, pos, now);
    var closest;
    if (pos) {
      closest = ranked[0];
    } else {
      var fallback = pickDefaultShop(shops);
      for (var i = 0; i < ranked.length; i++) {
        if (ranked[i].shop === fallback) { closest = ranked[i]; break; }
      }
      if (!closest) closest = ranked[0];
    }

    var today = closest.shop.hours && closest.shop.hours[now.day];
    var closeClock = (today && today.close) ? formatClockTime(today.close) : '';

    var viewModel = {
      state: closest.state,
      shop: closest.shop,
      closeClock: closeClock,
      distanceMi: pos ? closest.distanceMi : undefined,
      hasSpecialHours: !!(closest.shop.hasSpecialHours),
    };

    // Shipping-market visitors (>60mi from closest shop): swap the pill
    // to shipping-incentive mode instead of hiding it. Shows a dynamic
    // delivery-day estimate based on the shipping schedule.
    var isShippingMarket = !!(
      pos &&
      typeof closest.distanceMi === 'number' &&
      !isNaN(closest.distanceMi) &&
      closest.distanceMi > NEAR_THRESHOLD_MI
    );
    var shipping = isShippingMarket ? getShippingInfo() : null;
    // If the shared util didn't return a usable label (e.g., deferred script
    // hasn't loaded yet), fall back to the normal closest-shop pill so we
    // never render an empty shipping pill — better UX than just an icon.
    var renderShipping = isShippingMarket && shipping && shipping.dateLabel;

    for (var j = 0; j < pills.length; j++) {
      if (renderShipping) {
        applyShippingMode(pills[j], shipping);
      } else {
        removeShippingMode(pills[j]);
        applyToPill(pills[j], viewModel);
      }
      pills[j].removeAttribute('hidden');
    }

    try {
      document.dispatchEvent(new CustomEvent('whero:status-updated', {
        detail: {
          closestShop: closest.shop,
          state: closest.state,
          distanceMi: pos ? closest.distanceMi : null,
          hasSpecialHours: !!(closest.shop.hasSpecialHours),
          isShippingMarket: isShippingMarket,
          allShops: ranked,
        },
      }));
    } catch (_) { /* older browsers w/o CustomEvent constructor — ignored */ }

    return { closest: closest, ranked: ranked };
  }

  /* ---- init -------------------------------------------------------------- */

  var state = {
    shops: null,
    pills: [],
    position: null,
    lastRender: null,
    gpsRequested: false,
  };

  function refresh() {
    if (!state.shops || state.pills.length === 0) return;
    state.lastRender = renderAll(state.pills, state.shops, state.position);
  }

  function init() {
    var shops = readShops();
    if (!shops) {
      // Spec: render nothing, log once, do not crash.
      if (!window.__wheroStatusPillWarned) {
        window.__wheroStatusPillWarned = true;
        console.warn('[whero-status-pill] shops data missing or malformed; pill inactive');
      }
      return;
    }

    var pills = document.querySelectorAll(PILL_SELECTOR);
    if (pills.length === 0) return;

    state.shops = shops;
    state.pills = pills;

    for (var i = 0; i < pills.length; i++) {
      wirePill(pills[i]);
    }

    // Tier 1: cached position (<6h old).
    var cached = readCachedPosition();
    if (cached) {
      state.position = cached;
      refresh();
    } else {
      // Render with no distance first so the pill reflects current state
      // immediately even before the IP geo round-trip lands.
      refresh();
      // Tier 2: IP geolocation — refines the closest-shop selection.
      fetchIpGeo().then(function (pos) {
        if (!pos) return;
        state.position = pos;
        cachePosition(pos);
        refresh();
      });
    }
  }

  window.WheroStatusPill = {
    refresh: refresh,
    getState: function () {
      return {
        shops: state.shops,
        position: state.position,
        lastRender: state.lastRender,
      };
    },
  };

  /* ---- Toast pickup integration ---------------------------------------- */
  /* Fetches live pickup URLs from the Toast bridge and wires every
     [data-toast-redirect] element on the page. Flavor-aware: elements
     with data-toast-item-id get a deeplink to that specific item.      */

  var BRIDGE_BASE = window.WHERO_TOAST_BRIDGE_URL || 'https://api.wynstons.com';
  var BRIDGE_TIMEOUT_MS = 3000;
  var PICKUP_CACHE_TTL_MS = 2 * 60 * 1000; // 2 min
  var FALLBACK_URL = 'https://wynstons.order.toasttab.com/';
  var FALLBACK_QUOTE = 20;

  var pickupCache = {}; // { key: { data, ts } }

  function pickupCacheKey(shopSlug, itemId) {
    return 'whero_pickup_' + shopSlug + (itemId ? '_' + itemId : '');
  }

  function readPickupCache(key) {
    // Try sessionStorage first.
    try {
      var raw = sessionStorage.getItem(key);
      if (raw) {
        var parsed = JSON.parse(raw);
        if (parsed && Date.now() - parsed.ts < PICKUP_CACHE_TTL_MS) return parsed.data;
      }
    } catch (_) {}
    // In-memory fallback.
    var mem = pickupCache[key];
    if (mem && Date.now() - mem.ts < PICKUP_CACHE_TTL_MS) return mem.data;
    return null;
  }

  function writePickupCache(key, data) {
    var entry = { data: data, ts: Date.now() };
    pickupCache[key] = entry;
    try { sessionStorage.setItem(key, JSON.stringify(entry)); } catch (_) {}
  }

  function fetchPickupUrl(shopSlug, itemId) {
    var key = pickupCacheKey(shopSlug, itemId);
    var cached = readPickupCache(key);
    if (cached) return Promise.resolve(cached);

    var url = BRIDGE_BASE + '/api/toast/pickup-url?shop=' + encodeURIComponent(shopSlug);
    if (itemId) url += '&item=' + encodeURIComponent(itemId);

    var ctl;
    try { ctl = new AbortController(); } catch (_) { ctl = null; }
    var timer = ctl ? setTimeout(function () { ctl.abort(); }, BRIDGE_TIMEOUT_MS) : null;
    var opts = ctl ? { signal: ctl.signal } : {};

    return fetch(url, opts).then(function (r) {
      if (timer) clearTimeout(timer);
      if (!r.ok) return null;
      return r.json();
    }).then(function (data) {
      if (!data) return null;
      writePickupCache(key, data);
      return data;
    }).catch(function () {
      if (timer) clearTimeout(timer);
      return null;
    });
  }

  function wireToastRedirects(closestShop, shopState) {
    if (!closestShop) return;

    var shopSlug = closestShop.name
      ? closestShop.name.toLowerCase().replace(/\s+/g, '-')
      : '';
    if (!shopSlug) return;

    var elements = document.querySelectorAll('[data-toast-redirect]');
    if (elements.length === 0) return;

    // Group elements: with item ID (flavor-aware) vs without (generic).
    var generic = [];
    var flavored = {};
    for (var i = 0; i < elements.length; i++) {
      var el = elements[i];
      var itemId = el.getAttribute('data-toast-item-id');
      if (itemId) {
        if (!flavored[itemId]) flavored[itemId] = [];
        flavored[itemId].push(el);
      } else {
        generic.push(el);
      }
    }

    // Fetch generic pickup URL for CTAs without item IDs.
    fetchPickupUrl(shopSlug).then(function (data) {
      var pickupData = data || { url: FALLBACK_URL, quote_minutes: FALLBACK_QUOTE, is_accepting_orders: true };
      for (var j = 0; j < generic.length; j++) {
        applyPickupToElement(generic[j], pickupData, shopSlug, shopState);
      }
      // Companion pickup pills — just update href. We don't run
      // applyPickupToElement on them because its text-swap fallback
      // (looking for .pickup-link and replacing element text) would
      // clobber the companion's icon + label + chevron structure.
      var companions = document.querySelectorAll('[data-role="order-cta-desktop"]');
      if (pickupData && pickupData.url) {
        for (var c = 0; c < companions.length; c++) {
          companions[c].setAttribute('href', pickupData.url);
        }
      }
    });

    // Fetch item-specific URLs for flavor-aware CTAs.
    var itemIds = Object.keys(flavored);
    for (var k = 0; k < itemIds.length; k++) {
      (function (iid) {
        fetchPickupUrl(shopSlug, iid).then(function (data) {
          if (!data) return; // Leave the hardcoded URL in place.
          var els = flavored[iid];
          for (var m = 0; m < els.length; m++) {
            applyPickupToElement(els[m], data, shopSlug, shopState);
          }
        });
      })(itemIds[k]);
    }
  }

  function applyPickupToElement(el, data, shopSlug, shopState) {
    // Set the href.
    if (data.url) {
      el.setAttribute('href', data.url);
    }

    // Update quote time text if element has a data-quote-target.
    var quoteTarget = el.querySelector('[data-quote-time]');
    if (quoteTarget && data.quote_minutes) {
      quoteTarget.textContent = '~' + data.quote_minutes + ' min';
    }

    // If not accepting orders OR shop is closing → disable + swap text.
    if (data.is_accepting_orders === false || shopState === 'is-closed') {
      el.classList.add('is-disabled');
      var textSpan = el.querySelector('.pickup-link') || el;
      if (textSpan.textContent.indexOf('tomorrow') === -1) {
        textSpan.textContent = 'Order for tomorrow';
      }
      el.addEventListener('click', function (e) {
        if (el.classList.contains('is-disabled')) e.preventDefault();
      });
    } else {
      el.classList.remove('is-disabled');
    }

    // Fire analytics on click (if gtag is defined).
    el.addEventListener('click', function () {
      if (typeof gtag === 'function') {
        var source = el.getAttribute('data-source') || 'cta';
        try { gtag('event', 'pickup_click', { shop: shopSlug, source: source }); } catch (_) {}
      }
    });
  }

  // Listen for closest-shop updates and wire Toast redirects.
  document.addEventListener('whero:status-updated', function (e) {
    var d = (e && e.detail) || {};
    if (d.isShippingMarket) return; // Shipping visitors don't use Toast pickup.
    wireToastRedirects(d.closestShop, d.state);
  });

  window.WheroToast = {
    refreshPickup: function () {
      if (state.lastRender && state.lastRender.closest) {
        wireToastRedirects(state.lastRender.closest.shop, state.lastRender.closest.state);
      }
    },
    getCache: function () {
      return pickupCache;
    },
  };

  // Wait for DOMContentLoaded even when readyState is 'interactive'. Reason:
  // this script is deferred, and our sibling deferred script
  // (whero-shipping-schedule.js) appears later in the document. Both run
  // before DOMContentLoaded, but the 'interactive' window happens BETWEEN
  // their executions — if we init now we'd miss WheroShipping. DOMContentLoaded
  // is the earliest point where all defer scripts have finished evaluating.
  if (document.readyState === 'complete') {
    init();
  } else {
    document.addEventListener('DOMContentLoaded', init);
  }
})();
